import renderMarkdown from "@/lib/markdown";

const SECTIONS = [
  { key: "overview", label: "Overview", icon: "📋" },
  { key: "important_dates_md", label: "Important Dates", icon: "📅" },
  { key: "vacancy_breakdown", label: "Vacancy Details", icon: "👥" },
  { key: "eligibility_details", label: "Eligibility", icon: "🎓" },
  { key: "application_process", label: "How to Apply", icon: "📝" },
  { key: "selection_process_md", label: "Selection Process", icon: "🎯" },
  { key: "exam_pattern", label: "Exam Pattern", icon: "📊" },
  { key: "syllabus", label: "Syllabus", icon: "📚" },
  { key: "salary_details", label: "Salary", icon: "💰" },
  { key: "career_growth", label: "Career Growth", icon: "📈" },
  { key: "admit_card_info", label: "Admit Card", icon: "🎫" },
  { key: "result_info", label: "Result", icon: "📃" },
  { key: "cutoff_info", label: "Expected Cutoff", icon: "🎯" },
  { key: "exam_centres", label: "Exam Centres", icon: "📍" },
];

export default function JobDetailedSections({ job }) {
  // Only render if job has been enriched
  if (!job?.enriched) return null;
  const present = SECTIONS.filter((s) => job[s.key] && String(job[s.key]).trim().length > 0);
  const faqs = Array.isArray(job.faqs) ? job.faqs.filter((f) => f && f.q && f.a) : [];
  if (present.length === 0 && faqs.length === 0) return null;

  const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  const updatedDate = job.updated_date || job.posted_date;
  const updatedDisplay = updatedDate
    ? new Date(updatedDate).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  const updatedIso = (updatedDate ? String(updatedDate) : new Date().toISOString()).slice(0, 10);

  return (
    <section className="mt-10" data-testid="job-detailed-sections">
      <div className="mb-5 flex flex-wrap items-center gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="job-last-updated">
        <span className="inline-flex w-2 h-2 bg-amber-500 rounded-full" aria-hidden="true"></span>
        <span>
          Last updated by{" "}
          <a href="/about" className="font-semibold text-slate-900 hover:text-orange-600">Dileshwar Vitthale</a>
          {" "}on{" "}
          <time dateTime={updatedIso} className="font-semibold text-slate-900">{updatedDisplay}</time>
        </span>
        <span className="hidden sm:inline text-slate-400">·</span>
        <span className="text-amber-800 font-semibold">In-depth guide verified against official notification</span>
      </div>
      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Sticky Table of Contents */}
        <aside className="hidden lg:block">
          <div className="sticky top-20">
            <div className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-3">On this page</div>
            <nav aria-label="Job details table of contents">
              <ul className="space-y-1 text-sm">
                {present.map((s) => (
                  <li key={s.key}>
                    <a href={`#${slug(s.label)}`} className="block py-1.5 text-slate-700 hover:text-orange-600 transition-colors">
                      <span className="mr-1.5" aria-hidden="true">{s.icon}</span>{s.label}
                    </a>
                  </li>
                ))}
                {faqs.length > 0 && (
                  <li><a href="#faqs" className="block py-1.5 text-slate-700 hover:text-orange-600 transition-colors"><span className="mr-1.5" aria-hidden="true">❓</span>FAQs</a></li>
                )}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div>
          {present.map((s) => (
            <article key={s.key} id={slug(s.label)} className="bg-white border border-slate-200 rounded-lg p-5 md:p-6 mb-5 scroll-mt-20" data-testid={`section-${s.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <span aria-hidden="true" className="text-2xl">{s.icon}</span>
                <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>{s.label}</h2>
              </div>
              <div dangerouslySetInnerHTML={{ __html: renderMarkdown(String(job[s.key])) }} />
            </article>
          ))}

          {faqs.length > 0 && (
            <article id="faqs" className="bg-white border border-slate-200 rounded-lg p-5 md:p-6 mb-5 scroll-mt-20" data-testid="section-faqs">
              <div className="flex items-center gap-2 mb-3">
                <span aria-hidden="true" className="text-2xl">❓</span>
                <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>Frequently Asked Questions</h2>
              </div>
              <div className="space-y-2">
                {faqs.map((f, i) => (
                  <details key={i} className="group bg-slate-50 border border-slate-200 rounded-md p-3 hover:border-orange-300 transition-colors" data-testid={`job-faq-${i}`}>
                    <summary className="cursor-pointer min-h-11 inline-flex items-start font-semibold text-slate-900 text-sm md:text-base list-none w-full">
                      <span className="flex-1 pr-2">{f.q}</span>
                      <span className="ml-2 text-orange-600 group-open:rotate-45 transition-transform text-xl leading-none shrink-0" aria-hidden="true">+</span>
                    </summary>
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </article>
          )}
        </div>
      </div>
    </section>
  );
}
